import { Autocomplete, FormControl, Grid, TextField } from '@mui/material';
import React, {
    Fragment,
    useCallback,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useMemo,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import DetailedFundingSources from '../../defaults/DetailedFundingSources.json';
import FieldNames from '../../defaults/FieldNames.json';
import BasicTextField from '../details/customPanels/BasicTextField';
import DetailsPopover from '../details/customPanels/DetailsPopover';
import { isFeaturePostFire } from '../../utils/FeatureHelper';
import type { RootState } from '../../redux/store';


// ─── Aliases ─────────────────────────────────────────────────────────────────

const DFS = FieldNames.detailedFundingSources;
const FF = FieldNames.fundingFields;
const FEF = FieldNames.featureFields;
const YES = FieldNames.yesNo.yes;
const NO = FieldNames.yesNo.no;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getRowValue = (row, field) => row?.[field] ?? '';
const includesString = (str, sub) => Boolean(str?.toLowerCase()?.includes(sub?.toLowerCase()));

// ─── Sub-component: reusable labelled autocomplete row ───────────────────────

function SourceAutocomplete({
    id,
    label,
    value,
    options,
    fieldKey,
    requiredFields = [],
    additionalRequiredFields = [],
    isPoint,
    onChange,
    filterOptions,
}) {
    const isRequired =
        requiredFields.includes(fieldKey) || additionalRequiredFields.includes(fieldKey);

    return (
        <FormControl variant='standard' sx={{ margin: '15px', minWidth: 120, width: '90%' }}>
            <Grid container direction='row' size={12} style={{ alignItems: 'center' }}>
                <Grid size={6}>
                    <span>
                        <label>{label}</label>
                        {isRequired && <span className='required'>*</span>}
                        <DetailsPopover
                            mode='edit'
                            field={fieldKey}
                            categoryValue={null}
                            isPoint={isPoint ? YES : NO}
                            agencyValue={null}
                            isBulkApproval={false}
                        />
                    </span>
                </Grid>
                <Grid size={6}>
                    <Autocomplete
                        id={id}
                        value={value ?? ''}
                        options={options}
                        onChange={onChange}
                        filterOptions={filterOptions}
                        isOptionEqualToValue={(opt, val) => opt === val}
                        getOptionLabel={(opt) => opt ?? ''}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {params?.InputProps?.endAdornment}
                                            </React.Fragment>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </FormControl>
    );
}

// ─── Option-filtering helpers ────────────────────────────────────────────────
//
// Extracted and consolidated from the ~200 lines of duplicated inline
// `filterOptions` logic in the legacy component.

/**
 * Given a flat list of detailed-funding-source objects, return a de-duped
 * array of string values for `field`, filtered by BIL / BAR / "all" flags
 * and the current parent selections (program → category → subCategory).
 */
function getFilteredOptions(allSources, { isBil, isBar, isAll }, field, { program, category }) {
    const parentMatches = (src) => {
        if (field === DFS.fundingSourceProgram) return true;
        if (field === DFS.fundingSourceCategory) return src[DFS.fundingSourceProgram] === program;
        if (field === DFS.fundingSourceSubCategory)
            return (
                src[DFS.fundingSourceCategory] === category &&
                src[DFS.fundingSourceProgram] === program
            );
        return true;
    };

    const flagMatches = (src) => {
        const isContributed = src[FieldNames.fundingSources.isContributed] === `${YES}`;
        if (isAll) return true; // show everything
        if (isBil) return includesString(src[DFS.fundingSourceCategory], 'BIL') || isContributed;
        if (isBar)
            return (
                (includesString(src[DFS.fundingSourceCategory], 'BAR') &&
                    !includesString(src[DFS.fundingSourceCategory], 'BIL')) ||
                isContributed
            );
        // neither BIL nor BAR
        return (
            (!includesString(src[DFS.fundingSourceCategory], 'BIL') &&
                !includesString(src[DFS.fundingSourceCategory], 'BAR')) ||
            isContributed
        );
    };

    const seen = new Set();
    return allSources.reduce((acc, src) => {
        const val = src[field];
        if (val && !seen.has(val) && parentMatches(src) && flagMatches(src)) {
            seen.add(val);
            acc.push(val);
        }
        return acc;
    }, []);
}

/**
 * Analyse sibling `rows` (excluding the row being edited) to determine
 * whether the user is constrained to BIL / BAR options, and optionally
 * narrow the source list to post-fire or fuels only.
 *
 * Returns { isBil, isBar, isAll, filteredSources }.
 */
function computeRowFlags(rows, selectedRow, allSources, program, category) {
    const noRows = !rows?.length;
    const isSingleEdit = rows?.length === 1 && selectedRow;

    // Nothing to constrain against
    if (noRows || isSingleEdit) {
        return { isBil: false, isBar: false, isAll: true, filteredSources: allSources };
    }

    // Exclude the row we're editing
    const remaining = selectedRow ? rows.filter((r) => r[FF.id] !== selectedRow[FF.id]) : rows;

    if (!remaining.length) {
        // We were the only non-contributed row – decide from current selection
        if (program && !includesString(program, 'Fuels') && includesString(category, 'BIL'))
            return { isBil: true, isBar: false, isAll: false, filteredSources: allSources };
        if (program && !includesString(program, 'Fuels') && includesString(category, 'BAR'))
            return { isBil: false, isBar: true, isAll: false, filteredSources: allSources };
        return { isBil: false, isBar: false, isAll: true, filteredSources: allSources };
    }

    const onlyContributed = remaining.every((r) =>
        includesString(r[DFS.fundingSourceProgram], 'Contributed')
    );
    if (onlyContributed) {
        return { isBil: false, isBar: false, isAll: true, filteredSources: allSources };
    }

    // Non-BIL, non-BAR, non-contributed rows exist → restrict to same program family.
    // Check postfire FIRST — postfire rows can have "BAR" in their category name
    // (e.g. "BAR - Regular"), which would incorrectly trigger the hasBar branch below
    // if we don't handle them here first.
    const nonContributed = remaining.filter(
        (r) => !includesString(r[FF.fundingSourceProgram], 'Contributed')
    );
    const hasPostFire = nonContributed.some((r) =>
        includesString(r[FF.fundingSourceProgram], FieldNames.fundingSourcePrograms.postFire)
    );

    if (hasPostFire) {
        // Postfire features always include contributed + fuels, plus postfire sources.
        const filtered = allSources.filter(
            (s) =>
                s[DFS.isPostFire] === YES ||
                s[DFS.isFuels] === YES ||
                s[FieldNames.fundingSources.isContributed] === `${YES}`
        );
        return { isBil: false, isBar: false, isAll: true, filteredSources: filtered };
    }

    const hasBil = remaining.some((r) => includesString(r[DFS.fundingSourceCategory], 'BIL'));
    const hasBar = remaining.some((r) => includesString(r[DFS.fundingSourceCategory], 'BAR'));

    if (hasBil) return { isBil: true, isBar: false, isAll: false, filteredSources: allSources };
    if (hasBar) return { isBil: false, isBar: true, isAll: false, filteredSources: allSources };

    // Remaining rows are fuels-only
    const filtered = allSources.filter(
        (s) =>
            s[DFS.isFuels] === YES || s[FieldNames.fundingSources.isContributed] === `${YES}`
    );
    return { isBil: false, isBar: false, isAll: false, filteredSources: filtered };
}

// ─── Main component ───────────────────────────────────────────────────────────
//
// Props (minimal – no prop-drilling of individual setters):
//   selectedRow      – row being edited (initial values)
//   editableFeature  – parent feature graphic (used to filter programs)
//   rows             – sibling funding rows (BIL / BAR detection)
//   requiredFields   – field keys that are mandatory
//   onChange         – (values) => void, fires on every change
//
// Imperative handle (via ref):
//   getValues()  – snapshot of current selections
//   isValid()    – whether required fields are satisfied
//

interface FundingSourceAssignmentProps {
    selectedRow?: any;
    editableFeature?: any;
    rows?: any[];
    requiredFields?: string[];
    isPoint?: boolean;
    onChange?: (values: any) => void;
    onValidityChange?: (valid: boolean) => void;
}

const FundingSourceAssignment = forwardRef<any, FundingSourceAssignmentProps>(
    function FundingSourceAssignment(
        {
            selectedRow,
            editableFeature,
            rows,
            requiredFields = [],
            isPoint = false,
            onChange,
            onValidityChange,
        },
        ref
    ) {
        // ── Read user data from Redux (no prop drilling) ──────────────────────────
        const userInfo = useSelector((state: RootState) => state.globalContext.userInfo);

        // ── Local state ───────────────────────────────────────────────────────────
        const [program, setProgram] = useState('');
        const [category, setCategory] = useState('');
        const [subCategory, setSubCategory] = useState('');
        const [fundingSource, setFundingSource] = useState('');
        const [functionalArea, setFunctionalArea] = useState('');
        const [contributedNotes, setContributedNotes] = useState('');
        const [isBil, setIsBil] = useState(false);
        const [isBar, setIsBar] = useState(false);
        const [isContributed, setIsContributed] = useState(false);
        const [additionalRequiredFields, setAdditionalRequiredFields] = useState([]);

        // ── Build the eligible funding-source list from static JSON + user role ───
        const fundingSourceData = useMemo(() => {
            const all = DetailedFundingSources.fundingSources ?? [];
            if (!all.length || !userInfo) return [];

            const isUserPostFire = userInfo[FieldNames.userFields.isPostFire] === YES;
            const isUserFuels = userInfo[FieldNames.userFields.isFuels] === YES;
            const featureAttr = editableFeature?.attributes;
            const isInteragency = featureAttr?.[FEF.isInteragency] === YES;

            const contributed = all.filter((s) => s[DFS.isContributed] === YES);
            const isFeaturePostFireFlag = isFeaturePostFire(featureAttr);
            const postFire =
                isUserPostFire && isFeaturePostFireFlag
                    ? all
                          .filter((s) => s[DFS.isPostFire] === YES && s[DFS.isContributed] === NO)
                          .filter((s) =>
                              featureAttr?.Program
                                  ? s.FundingSource.includes(featureAttr.Program)
                                  : true
                          )
                    : [];
            const fuels = isUserFuels
                ? all
                      .filter((s) => s[DFS.isFuels] === YES && s[DFS.isContributed] === NO)
                      .filter(
                          (s) => isInteragency || s.FundingSource !== 'Fuels Interagency Support'
                      )
                : [];

            return [...contributed, ...postFire, ...fuels];
        }, [userInfo, editableFeature]);

        // ── Resolve FundingSource + FunctionalArea from current selections ────────
        const resolveFundingSource = useCallback(
            (prog, cat, subCat) => {
                const match = fundingSourceData.find(
                    (s) =>
                        s[DFS.fundingSourceProgram] === prog &&
                        s[DFS.fundingSourceCategory] === cat &&
                        (!subCat || s[DFS.fundingSourceSubCategory] === subCat)
                );
                setFundingSource(match?.[DFS.fundingSource] ?? '');
                setFunctionalArea(match?.FunctionalArea ?? 'XXXXXXXXX.XXXXXX');
            },
            [fundingSourceData]
        );

        // const buildCostCode(costCenter, )


        // ── Build values snapshot ─────────────────────────────────────────────────
        const buildValues = useCallback(
            () => ({
                [FF.fundingSourceProgram]: program || null,
                [FF.fundingSourceCategory]: category || null,
                [FF.fundingSourceSubCategory]: subCategory || null,
                [FF.fundingSource]: fundingSource || null,
                [FF.functionalArea]: functionalArea || 'XXXXXXXXX.XXXXXX',
                [FF.notes]: contributedNotes || null,
                [FF.isContributed]: isContributed ? YES : NO,
        //         [FF.costCode] =
        // `${currentRecord[FieldNames.fundingFields.costCenter] ? currentRecord[FieldNames.fundingFields.costCenter] : 'XXXXXXXXXX'}/${currentRecord[FieldNames.fundingFields.functionalArea] ? currentRecord[FieldNames.fundingFields.functionalArea] : 'XXXXXXXXX.XXXXXX'}/${parentAttributes && parentAttributes.attributes[FieldNames.featureFields.wbsID] ? parentAttributes[FieldNames.featureFields.wbsID] : 'XXXXXXXXXXXX'}`;
        //
                isBil,
                isBar,
            }),
            [
                program,
                category,
                subCategory,
                fundingSource,
                functionalArea,
                contributedNotes,
                isBil,
                isBar,
                isContributed
            ]
        );

        // ── Notify parent on every change ─────────────────────────────────────────
        useEffect(() => {
            onChange?.(buildValues());
        }, [buildValues, onChange]);

        // ── Validation ────────────────────────────────────────────────────────────
        const checkValidity = useCallback(() => {
            const subCatRequired = additionalRequiredFields.includes(FF.fundingSourceSubCategory);
            return Boolean(program && category && (!subCatRequired || subCategory));
        }, [program, category, subCategory, additionalRequiredFields]);

        // ── Notify parent when validity changes ───────────────────────────────────
        useEffect(() => {
            onValidityChange?.(checkValidity());
        }, [checkValidity, onValidityChange]);

        // ── Imperative handle ─────────────────────────────────────────────────────
        useImperativeHandle(
            ref,
            () => ({
                getValues: buildValues,
                isValid: checkValidity,
            }),
            [buildValues, checkValidity]
        );

        // ── Initialise from selectedRow ───────────────────────────────────────────
        useEffect(() => {
            if (!selectedRow) return;
            setProgram(getRowValue(selectedRow, FF.fundingSourceProgram));
            setCategory(getRowValue(selectedRow, FF.fundingSourceCategory));
            setSubCategory(getRowValue(selectedRow, FF.fundingSourceSubCategory));
            setFundingSource(getRowValue(selectedRow, FF.fundingSource));
            setFunctionalArea(getRowValue(selectedRow, FF.functionalArea));
            setContributedNotes(getRowValue(selectedRow, FF.notes));
            setIsContributed(includesString(getRowValue(selectedRow, FF.fundingSourceProgram), 'contributed'));
            setIsBil( includesString(getRowValue(selectedRow, FF.fundingSourceCategory), 'BIL'));
            setIsBar( includesString(getRowValue(selectedRow, FF.fundingSourceCategory), 'BAR'));
            const catVal = getRowValue(selectedRow, FF.fundingSourceCategory);
            setAdditionalRequiredFields(
                catVal === FieldNames.budgetBuckets.bil ? [FF.fundingSourceSubCategory] : []
            );
        }, [selectedRow]);

        // ── BIL / BAR detection from sibling rows ─────────────────────────────────
        // useEffect(() => {
        //     if (!rows?.length) {
        //         setIsBil(false);
        //         setIsBar(false);
        //         return;
        //     }
        //     setIsBil(rows.some((r) => includes(r[FF.fundingSourceCategory], 'BIL')));
        //     setIsBar(rows.some((r) => includes(r[FF.fundingSourceCategory], 'BAR')));
        // }, [rows]);

        // ── Cascade-reset helpers ─────────────────────────────────────────────────
        const resetBelowProgram = () => {
            setCategory('');
            setSubCategory('');
            setFundingSource('');
            setFunctionalArea('');
            setContributedNotes('');
        };
        const resetBelowCategory = () => {
            setSubCategory('');
            setFundingSource('');
            setFunctionalArea('');
        };

        // ── Shared autocomplete props ─────────────────────────────────────────────
        const sharedProps = {
            requiredFields,
            additionalRequiredFields,
            isPoint,
        };

        // ── Render ────────────────────────────────────────────────────────────────
        const showContributedNotes = includesString(program, 'Contributed');
        const showSubCategory = includesString(category, 'BIL') && !includesString(program, 'Post-Fire');

        return (
            <Fragment>
                {/* ── Program ──────────────────────────────────────────────────── */}
                <SourceAutocomplete
                    id='programSelection'
                    label='Funding Source Program'
                    value={program}
                    options={fundingSourceData}
                    fieldKey={FEF.fundingSourceProgram}
                    {...sharedProps}
                    filterOptions={(options) => {
                        const { isAll, filteredSources } = computeRowFlags(
                            rows,
                            selectedRow,
                            options,
                            program,
                            category
                        );
                        return getFilteredOptions(
                            filteredSources,
                            { isBil: false, isBar: false, isAll },
                            DFS.fundingSourceProgram,
                            { program, category }
                        );
                    }}
                    onChange={(_, v) => {
                       setProgram(v ?? '');
                        resetBelowProgram();
                         if (!v && (!rows?.length || (rows?.length === 1 && selectedRow))) {
                            setIsBil(false);
                            setIsBar(false);
                        }
                        setIsContributed(includesString(v, 'contributed'));
                    }}
                />

                {/* ── Category ─────────────────────────────────────────────────── */}
                <SourceAutocomplete
                    id='categorySelection'
                    label='Funding Source Category'
                    value={category}
                    options={fundingSourceData}
                    fieldKey={FEF.fundingSourceCategory}
                    {...sharedProps}
                    filterOptions={(options) => {
                        const {
                            isBil: b,
                            isBar: ba,
                            isAll,
                            filteredSources,
                        } = computeRowFlags(rows, selectedRow, options, program, category);
                        return getFilteredOptions(
                            filteredSources,
                            { isBil: b, isBar: ba, isAll },
                            DFS.fundingSourceCategory,
                            { program, category }
                        );
                    }}
                    onChange={(_, v) => {
                        setCategory(v ?? '');
                        resetBelowCategory();

                        if (!v) {
                            if (!rows?.length || (rows?.length === 1 && selectedRow)) {
                                setIsBil(false);
                                setIsBar(false);
                            }
                            setAdditionalRequiredFields([]);
                            return;
                        }

                        const isBilCategory = includesString(v, 'BIL');
                        const isPostFireProgram =
                            program === FieldNames.fundingSourcePrograms.postFire;

                        if (!isBilCategory || isPostFireProgram) {
                            // Non-BIL, or Post-Fire BIL → resolve immediately (no sub-category)
                            resolveFundingSource(program, v, '');
                            setIsBil(false);
                            setIsBar(false);
                            setAdditionalRequiredFields([]);
                        } else {
                            // BIL selected → require sub-category before resolving
                            setIsBil(isBilCategory);
                            setIsBar(includesString(v, 'BAR'));
                            setAdditionalRequiredFields([FF.fundingSourceSubCategory]);
                        }
                    }}
                />

                {/* ── Contributed Notes (shown only for "Contributed" program) ── */}
                {showContributedNotes && (
                    <FormControl
                        variant='standard'
                        sx={{ margin: '15px', minWidth: 120, width: '90%' }}
                    >
                        <Grid container direction='row' size={12} style={{ alignItems: 'center' }}>
                            <Grid size={6}>
                                <span>
                                    <label>Notes</label>
                                    {requiredFields.includes(FEF.contributedNotes) && (
                                        <span className='required'>*</span>
                                    )}
                                    <DetailsPopover
                                        mode='edit'
                                        field={FEF.contributedNotes}
                                        categoryValue={null}
                                        isPoint={isPoint ? YES : NO}
                                        agencyValue={null}
                                        isBulkApproval={false}
                                    />
                                </span>
                            </Grid>
                            <Grid size={6}>
                                <BasicTextField
                                    field='contributedNotes'
                                    label='Contributed Notes'
                                    value={contributedNotes}
                                    setValue={setContributedNotes}
                                    variant='outlined'
                                    placeholder='Enter notes'
                                    useFullWidth
                                    useMultiline
                                />
                            </Grid>
                        </Grid>
                    </FormControl>
                )}

                {/* ── SubCategory (BIL + non-Post-Fire only) ───────────────────── */}
                {showSubCategory && (
                    <SourceAutocomplete
                        id='subCategorySelection'
                        label='Funding Source SubCategory'
                        value={subCategory}
                        options={fundingSourceData}
                        fieldKey={FEF.fundingSourceSubCategory}
                        {...sharedProps}
                        filterOptions={(options) =>
                            getFilteredOptions(
                                options,
                                { isBil: false, isBar: false, isAll: true },
                                DFS.fundingSourceSubCategory,
                                { program, category }
                            )
                        }
                        onChange={(_, v) => {
                            setSubCategory(v ?? '');
                            resolveFundingSource(program, category, v);
                        }}
                    />
                )}
            </Fragment>
        );
    }
);

export default FundingSourceAssignment;
